import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * The cards and connectors a flow is drawn with.
 *
 * Lifted out of the cadence builder, which draws a straight run of phases, so the intake builder
 * can draw a tree with the same pieces: one look for every flow in the console, and no graph
 * library. CSS only, so it stacks on a phone and reads in a screen reader.
 *
 * Named `StepCard`, not `Card`: `@/components/ui/card` already exports one and most pages import
 * it.
 */

export type Tone = "start" | "end" | "step" | "branch" | "flag" | "reject";

const TONES: Record<Tone, string> = {
  start: "bg-accent-9 text-white dark:text-gray-1",
  end: "bg-gray-12 text-gray-1 dark:bg-gray-11",
  step: "bg-accent-3 text-accent-11",
  branch: "bg-gray-4 text-text-muted",
  flag: "bg-warning-2 text-warning-11",
  reject: "bg-danger-2 text-danger-11",
};

export function StepCard({
  icon: Icon,
  tone,
  title,
  tour,
  invalid = false,
  actions,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  tone: Tone;
  title: string;
  /** The card's name in the page's tour. Optional: a card the tour never points at has none. */
  tour?: string;
  /** Something is wrong with this step, so it wears the error border. */
  invalid?: boolean;
  /** The dots menu, drawn at the card's top right. */
  actions?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <article
      className={cn(
        "flex gap-3 rounded-xl border bg-surface px-4 py-3 shadow-xs",
        invalid ? "border-danger-9" : "border-border",
      )}
      aria-label={title}
      {...(tour ? { "data-tour": tour } : {})}
    >
      <span
        className={cn("grid size-8 shrink-0 place-items-center rounded-lg", TONES[tone])}
        aria-hidden
      >
        <Icon className="size-4" />
      </span>
      <div className="min-w-0 flex-1 space-y-1 text-sm leading-7 text-text-muted">
        <h3 className="text-sm font-semibold leading-8 text-text">{title}</h3>
        {children}
      </div>
      {actions ? <div className="shrink-0">{actions}</div> : null}
    </article>
  );
}

/**
 * The line between two cards, with the plus that adds one on it.
 *
 * `label` names the branch the line is: on a tree it carries the condition's words, which is how
 * the canvas reads as a sentence rather than a diagram.
 */
export function Connector({
  label,
  children,
}: {
  label?: string | null;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center">
      <span className="h-3 w-px bg-border-strong" aria-hidden />
      {label ? (
        <span className="rounded-full border border-border bg-surface-sunken px-2 py-0.5 text-[11px] font-medium text-text-muted">
          {label}
        </span>
      ) : null}
      {children}
      <span className="h-3 w-px bg-border-strong" aria-hidden />
    </div>
  );
}

/** The dotted line down a column of steps. Needs a `relative` parent. */
export function Rail() {
  return (
    <span
      className="absolute bottom-0 left-3 top-0 w-px border-l border-dashed border-border-strong"
      aria-hidden
    />
  );
}

/**
 * A split's branches, side by side on a wide screen and stacked on a phone.
 *
 * The bus across the top is what makes it read as one fan-out rather than a list of columns; a
 * branch below 48rem wears its own header instead, since the bus means nothing stacked.
 */
export function BranchRow({ children }: { children: React.ReactNode }) {
  return (
    // The row stays inside its column and scrolls within itself when the branches need more
    // room than the page has. It must never reach outside its parent: a viewport-width negative
    // margin ignores the sidebar, so the left-hand branch slides underneath it and is cut off.
    <div className="relative">
      <span
        className="absolute left-0 right-0 top-0 hidden h-px bg-border-strong md:block"
        aria-hidden
      />
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:gap-4 md:overflow-x-auto md:pb-2 md:pt-3">
        {children}
      </div>
    </div>
  );
}

export function Branch({
  label,
  index,
  total,
  fallback = false,
  children,
}: {
  label: string;
  index: number;
  total: number;
  /** The last branch, which has no conditions and takes everyone no other branch named. */
  fallback?: boolean;
  children: React.ReactNode;
}) {
  return (
    // Columns share the row's width, down to a floor: past three or four branches the row
    // scrolls rather than squeezing every card until its words wrap to nothing.
    <section
      className="flex-1 space-y-3 md:min-w-[14rem] md:basis-0"
      aria-label={fallback ? `${label}, the base flow` : label}
    >
      <p className="text-xs font-medium text-text-muted md:hidden">
        Branch {index + 1} of {total}
      </p>
      <div className="flex justify-center">
        <span
          className={cn(
            "rounded-full border px-3 py-1 text-xs font-semibold",
            fallback
              ? "border-dashed border-border-strong text-text-muted"
              : "border-accent-7 bg-accent-3 text-accent-11",
          )}
        >
          {label}
        </span>
      </div>
      {children}
    </section>
  );
}

/** Where a split's branches come back together, said once under the row. */
export function MergeNote({ title }: { title: string }) {
  return (
    <p className="text-center text-xs text-text-muted">
      Branches rejoin at <span className="font-medium text-text">{title}</span>
    </p>
  );
}
